/**
 * Pricing, and the start of a purchase.
 *
 * The prices come from the database catalogue, not from this file. The old version
 * of this page had the amount typed into the markup and a "complete purchase"
 * button that generated a key with Math.random — a page that took money-shaped
 * input and produced a licence-shaped string. Everything here is either a real
 * number from the catalogue or a redirect to a real payment page.
 *
 * A product's name and description arrive from the catalogue in Portuguese, because
 * that is what the receipt and the checkout button read. They are put through
 * `localizeProduct` before they are printed, so this page does not offer three
 * Portuguese paragraphs next to three English buttons. The amount, the currency and
 * the unlocked apps are never translated, because the database owns those.
 */

import React, { useEffect, useState } from 'react';
import { ArrowRight, Check, Loader2, ShieldCheck } from 'lucide-react';

import { fetchProducts, messageOf, startCheckout, type Product } from '../lib/api';
import { SUITE_PRODUCT_ID } from '../content/apps';
import { CONTACT_EMAIL } from '../content/contact';
import { appNameFor, hasApp, useAuth } from '../auth/AuthProvider';
import { format, useI18n } from '../i18n';
import { localizeProduct } from '../i18n/productCopy';
import { Link, useRouter } from '../router';
import { HipercubeLottie, planForProduct } from './HipercubeLottie';

/**
 * Which locale formats each currency.
 *
 * Keyed by currency rather than by page language on purpose. The seller is Brazilian
 * and the amounts are charged in BRL, so a BRL figure is formatted as BRL is written
 * in Brazil whatever language the page is being read in. EUR maps to `pt-PT` because
 * that is the formatting this catalogue has always used for it; changing it would
 * change a number on a payment page, which is not a change to make while translating.
 */
const CURRENCY_LOCALE: Readonly<Record<string, string>> = { BRL: 'pt-BR', USD: 'en-US', EUR: 'pt-PT' };

/**
 * Minor units to a price to show.
 *
 * The division is on the server's integer and this only formats, so the number
 * displayed and the number charged come from the same source. A price typed into
 * this file would drift from the catalogue the first time either changed.
 *
 * The guard is here because this value decides whether somebody is told a number
 * they are about to be charged. A catalogue entry missing its amount must not be
 * allowed to print "R$ NaN" next to a live Buy button: an amount that cannot be
 * read is not a price, and showing one would be worse than saying so.
 *
 * `unavailable` is passed in rather than imported, so this stays a pure function of
 * its arguments and says nothing about which language is on screen.
 */
function formatPrice(amountCents: number, currency: string, unavailable: string): string {
  if (!Number.isFinite(amountCents) || amountCents < 0) return unavailable;

  const locale = CURRENCY_LOCALE[currency] ?? 'pt-BR';
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amountCents / 100);
  } catch {
    // An unknown currency code throws in Intl. Showing the raw amount is better than
    // a blank card.
    return `${(amountCents / 100).toFixed(2)} ${currency}`;
  }
}

type LoadState = 'loading' | 'ready' | 'failed';

export const PricingView: React.FC = () => {
  const { t } = useI18n();
  const [products, setProducts] = useState<readonly Product[]>([]);
  const [state, setState] = useState<LoadState>('loading');
  // Held as thrown, resolved at render, so a catalogue failure survives a language switch.
  const [failure, setFailure] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;

    fetchProducts()
      .then((catalogue) => {
        if (cancelled) return;
        setProducts(catalogue.products);
        setState('ready');
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setFailure(caught);
        setState('failed');
      });

    return () => {
      cancelled = true;
    };
    // `t` is stable for a given locale and the catalogue does not change language, so
    // re-reading the catalogue because the reader switched language would refetch the
    // same prices and throw away what is on screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="mx-auto w-full max-w-6xl px-5 py-20 sm:px-8 sm:py-28">
      <header className="mx-auto mb-16 max-w-2xl text-center">
        <h1 className="text-[clamp(2rem,4.4vw,3.1rem)] font-semibold leading-[1.06] tracking-[-0.028em] text-cream">
          {t.pricing.title}
        </h1>
        <p className="mt-6 text-[17px] leading-[1.55] text-muted">{t.pricing.body}</p>
      </header>

      {/*
        The three states are all rendered, rather than a spinner over an empty page.
        A failed catalogue read is the one that matters: it has to say the prices
        could not be loaded rather than showing an empty grid that reads as "nothing
        for sale".
      */}
      {state === 'loading' && (
        <div className="mx-auto grid max-w-4xl gap-5 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true">
          <span className="sr-only">{t.pricing.loading}</span>
          {[0, 1, 2].map((slot) => (
            // Skeletons shaped like the cards they replace. A spinner over an empty
            // page says "something is happening"; three cards with no prices say
            // "three plans are on the way", which is the information being waited on.
            <div key={slot} className="h-80 rounded-glass border border-line bg-panel/60" />
          ))}
        </div>
      )}

      {state === 'failed' && (
        <div className="glass mx-auto max-w-md rounded-glass p-8 text-center">
          <p className="text-sm leading-relaxed text-muted">
            {messageOf(failure, t.pricing.catalogueFailed)}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="pill pressable mt-6 border border-line-strong px-5 py-2.5 text-sm text-cream hover:bg-white/8"
          >
            {t.pricing.retry}
          </button>
        </div>
      )}

      {state === 'ready' && products.length === 0 && (
        <div className="glass mx-auto max-w-md rounded-glass p-8 text-center">
          <p className="text-sm leading-relaxed text-muted">
            {format(t.pricing.emptyBody, { email: CONTACT_EMAIL })}
          </p>
        </div>
      )}

      {state === 'ready' && products.length > 0 && (
        <div className="grid items-start gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} featured={product.id === SUITE_PRODUCT_ID} />
          ))}
        </div>
      )}

      <p className="mt-12 flex items-center justify-center gap-2 text-center text-sm text-muted">
        <ShieldCheck className="size-4 text-gold/80" aria-hidden="true" />
        {t.pricing.footerNote}
      </p>
    </section>
  );
};

const ProductCard: React.FC<{ product: Product; featured: boolean }> = ({ product, featured }) => {
  const { locale, t } = useI18n();
  const { status, entitlements, session } = useAuth();
  const { navigate } = useRouter();
  const [busy, setBusy] = useState(false);
  // Held as thrown, resolved at render. See the same note on the catalogue state.
  const [failure, setFailure] = useState<unknown>(null);

  // Only the two prose columns are translated. The amount, the currency and the
  // unlocked apps below are the catalogue's, untouched.
  const localised = localizeProduct(product, locale);

  // An entry that unlocks nothing is a broken catalogue row, not something the
  // reader already has: `every` over an empty array is true, which would have
  // labelled it "Já é seu" and disabled the only button that could report it.
  const alreadyOwnsEverything =
    product.unlocks.length > 0 && product.unlocks.every((appId) => hasApp(entitlements, appId));

  // A price the client could not read is not a price. The Buy button stays disabled
  // in that case: the card above it would say "Unavailable" while offering a
  // checkout for an amount this page never actually knew.
  const priceIsReadable = Number.isFinite(product.amountCents) && product.amountCents >= 0;
  const canBuy = priceIsReadable && !alreadyOwnsEverything && !busy;

  const buy = async () => {
    if (busy) return;

    // Signed out, the button is a link to the form rather than an error. Sending
    // somebody to a checkout that will refuse them is worse than asking first.
    if (status !== 'signed-in') {
      navigate('conta');
      return;
    }

    setBusy(true);
    setFailure(null);

    try {
      // `session` is read from the hook above rather than from a second `useAuth()`
      // call inside this function, which was a hook called outside a component and
      // would have thrown on any React that checked. Non-null because
      // `status === 'signed-in'` was checked just above; the empty string would only
      // be reachable if that check and this read disagreed.
      const result = await startCheckout(session?.accessToken ?? '', product.id);
      // A full navigation, not a client-side route: the payment page is on
      // Mercado Pago's domain and nothing of ours renders there.
      window.location.href = result.checkoutUrl;
    } catch (caught) {
      setFailure(caught);
      setBusy(false);
    }
  };

  return (
    <article
      className={`glass flex h-full flex-col rounded-glass p-7 ${
        featured ? 'border-gold/45 shadow-[0_0_0_1px_rgba(237,199,68,0.28),0_36px_90px_-30px_rgba(237,199,68,0.28)]' : ''
      }`}
    >
      <div className="mb-4 flex justify-center">
        <HipercubeLottie plan={planForProduct(product.id)} size={88} title={null} />
      </div>
      <div className="mb-1 flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-cream">{localised.name}</h2>
        {featured && (
          <span className="rounded-full border border-gold/40 px-2.5 py-0.5 text-[11px] font-medium text-gold">
            {t.pricing.bundleBadge}
          </span>
        )}
      </div>

      <p className="mb-6 text-sm leading-relaxed text-muted">{localised.description}</p>

      <p className="mb-1 font-mono text-[34px] font-semibold leading-none tracking-tight text-cream">
        {formatPrice(product.amountCents, product.currency, t.pricing.unavailable)}
      </p>
      <p className="mb-7 text-xs text-muted">{t.pricing.onceLabel}</p>

      <ul className="mb-6 flex-1 space-y-2.5">
        {product.unlocks.map((appId) => (
          <li key={appId} className="flex items-start gap-2 text-sm text-muted">
            <Check className="mt-0.5 size-4 shrink-0 text-gold" aria-hidden="true" />
            <span>{appNameFor(appId)}</span>
          </li>
        ))}
      </ul>

      <p role="alert" className="min-h-[1.25rem] text-xs text-danger">
        {messageOf(failure, t.pricing.checkoutFailed)}
      </p>

      <button
        type="button"
        onClick={() => void buy()}
        disabled={!canBuy}
        className={`pill pressable mt-2 w-full px-5 py-3 text-sm disabled:cursor-not-allowed disabled:opacity-60 ${
          !priceIsReadable
            ? 'border border-line-strong text-muted'
            : alreadyOwnsEverything
              ? 'border border-line-strong text-muted'
              : featured
                ? 'bg-gold text-ink hover:bg-gold-bright'
                : 'border border-line-strong text-cream hover:bg-white/8'
        }`}
      >
        {!priceIsReadable ? (
          t.pricing.unavailable
        ) : alreadyOwnsEverything ? (
          t.pricing.alreadyOwned
        ) : busy ? (
          <>
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            {t.pricing.openingPayment}
          </>
        ) : status !== 'signed-in' ? (
          <>
            {t.pricing.signInToBuy}
            <ArrowRight className="size-4" aria-hidden="true" />
          </>
        ) : (
          <>
            {t.pricing.buy}
            <ArrowRight className="size-4" aria-hidden="true" />
          </>
        )}
      </button>
    </article>
  );
};

/**
 * The closed banner for the pricing section on the home page.
 *
 * Composed rather than styled separately so the two cannot say different things
 * about what a purchase includes.
 */
export const PricingTeaser: React.FC = () => {
  const { t } = useI18n();

  return (
    <section className="px-5 py-24 sm:px-8 sm:py-28">
      <div className="glass mx-auto flex max-w-4xl flex-col items-start gap-8 rounded-glass p-9 sm:p-12 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="max-w-md text-[clamp(1.6rem,3.2vw,2.4rem)] font-semibold leading-[1.1] tracking-[-0.025em] text-cream">
            {t.pricing.teaserTitle}
          </h2>
          <p className="mt-4 max-w-md text-[15px] leading-[1.5] text-muted">{t.pricing.teaserBody}</p>
        </div>
        <Link
          to="planos"
          className="pill pressable shrink-0 bg-gold px-6 py-3.5 text-[15px] text-ink hover:bg-gold-bright"
        >
          <img src="/OrobasWorkLogoNewGold.svg" alt={t.pricing.teaserCta} width={17} height={17} className="block" />
          {t.pricing.teaserCta}
        </Link>
      </div>
    </section>
  );
};